// src/lib/utils/random.ts

/**
 * A function that returns a random number between 0 (inclusive) and 1 (exclusive).
 * @returns {number} A random number in the range [0, 1)
 */
export type RandomSource = () => number

/**
 * FNV-1a hash algorithm offset basis (32-bit).
 * This is the initial hash value used in the FNV-1a hashing algorithm.
 * @constant {number}
 */
const FNV_OFFSET = 0x811c9dc5

/**
 * FNV-1a hash algorithm prime number (32-bit).
 * This prime is used for multiplication in the FNV-1a hashing algorithm.
 * @constant {number}
 */
const FNV_PRIME = 0x01000193

/**
 * Ensures a number is treated as an unsigned 32-bit integer.
 * Uses zero-fill right shift to convert to uint32.
 * @param {number} value - The number to convert
 * @returns {number} The value as an unsigned 32-bit integer
 */
const ensureUint32 = (value: number): number => (value >>> 0)

/**
 * Converts a number to an unsigned 32-bit integer, handling non-finite values.
 * Non-finite values (NaN, Infinity, -Infinity) are converted to 0.
 * @param {number} value - The number to convert
 * @returns {number} The value as an unsigned 32-bit integer, or 0 if non-finite
 */
const toUint32 = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0
  }

  return ensureUint32(value)
}

/**
 * Mixes a hash value with a string using the FNV-1a algorithm.
 * Processes each character in the string to update the hash.
 * @param {number} hash - The current hash value (unsigned 32-bit integer)
 * @param {string} input - The string to mix into the hash
 * @returns {number} The updated hash value as an unsigned 32-bit integer
 */
const mixHashWithString = (hash: number, input: string): number => {
  let next = hash

  for (let index = 0; index < input.length; index += 1) {
    next ^= input.charCodeAt(index) & 0xff
    next = ensureUint32(Math.imul(next, FNV_PRIME))
  }

  return next
}

/**
 * Creates a deterministic hash from one or more seed inputs using the FNV-1a algorithm.
 * Numbers are converted to base-36 strings before hashing.
 * Returns the FNV offset basis if no inputs are provided.
 * @param {...Array<number | string>} inputs - Variable number of strings or numbers to hash
 * @returns {number} A deterministic unsigned 32-bit integer hash
 */
export function hashSeed(...inputs: Array<number | string>): number {
  let hash = FNV_OFFSET

  if (inputs.length === 0) {
    return hash
  }

  for (const input of inputs) {
    if (typeof input === 'number') {
      hash = mixHashWithString(hash, input.toString(36))
    } else {
      hash = mixHashWithString(hash, input)
    }
  }

  return ensureUint32(hash)
}

/**
 * Creates a deterministic pseudo-random number generator from a seed.
 * Uses a simple but effective xorshift-based PRNG algorithm.
 * The generator returns numbers in the range [0, 1) similar to Math.random().
 * @param {number} seed - The seed value (unsigned 32-bit integer). If 0, defaults to 0x1a2b3c4d
 * @returns {RandomSource} A RandomSource function that generates deterministic pseudo-random numbers
 */
export function createDeterministicRandom(seed: number): RandomSource {
  let state = seed >>> 0

  if (state === 0) {
    state = 0x1a2b3c4d
  }

  return () => {
    state += 0x6d2b79f5
    let t = state
    t = Math.imul(t ^ (t >>> 15), 1 | t)
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t)
    return ((t ^ (t >>> 14)) >>> 0) / 0x100000000
  }
}

/**
 * Creates a random seed value suitable for initializing a PRNG.
 * Attempts to use cryptographically secure randomness from the browser's Web Crypto API.
 * Falls back to a hash of timestamp and Math.random() if crypto is unavailable.
 * @returns {number} An unsigned 32-bit integer seed value
 */
export function createBrowserSeed(): number {
  if (typeof window !== 'undefined' && typeof window.crypto?.getRandomValues === 'function') {
    const array = new Uint32Array(1)
    window.crypto.getRandomValues(array)
    return array[0] ?? 0
  }

  const timestamp = typeof performance !== 'undefined' ? performance.now() : Date.now()
  const entropy = typeof Math.random === 'function' ? Math.random() : 0.123456789

  return hashSeed('runtime', toUint32(timestamp), toUint32(entropy * 0x100000000))
}
